import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { apdmPercent } from '@euler/functions/apdm/campaignFunnel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmDashboardResult } from '@euler/model/apdm';
import {
  LucideIcon,
  MessageCircleMore,
  Rocket,
  Star,
  Trophy,
  Users,
} from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { ApdmCard, ApdmIconTile } from '../components/ApdmSurface';

function Figure({
  icon,
  color,
  label,
  value,
}: {
  icon: LucideIcon;
  color: string;
  label: string;
  value: string;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}>
      <ApdmIconTile icon={icon} color={color} />
      <View>
        <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
          {label}
        </Typography.Text>
        <Typography.Text bold size={rpx(16)} color={aiColors.foreground}>
          {value}
        </Typography.Text>
      </View>
    </View>
  );
}

function Tile({
  icon,
  color,
  label,
  value,
  note,
  wide,
}: {
  icon: LucideIcon;
  color: string;
  label: string;
  value: string;
  note?: string;
  wide: boolean;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <ApdmCard
      style={{
        flexBasis: wide ? 0 : '46%',
        flexGrow: 1,
        padding: rpx(14),
      }}
    >
      <ApdmIconTile icon={icon} color={color} />
      <Typography.Text
        size={rpx(11)}
        color={aiColors.mutedForeground}
        style={{ marginTop: rpx(12) }}
      >
        {label}
      </Typography.Text>
      <Typography.Text bold size={rpx(20)} color={aiColors.foreground}>
        {value}
      </Typography.Text>
      {note ? (
        <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
          {note}
        </Typography.Text>
      ) : null}
    </ApdmCard>
  );
}

const number = (value: number) => value.toLocaleString('zh-CN');

/** The period's key numbers: one row on a wide screen, two on a phone. */
export const DashboardStats = memo(
  ({
    dashboard,
    running,
    wide,
  }: {
    dashboard: ApdmDashboardResult;
    running: number;
    wide: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const { funnel } = dashboard;
    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: rpx(10) }}>
        <Tile
          wide={wide}
          icon={Rocket}
          color={aiColors.primary}
          label="进行中活动"
          value={number(running)}
        />
        <ApdmCard
          style={{
            flexBasis: wide ? 0 : '46%',
            flexGrow: wide ? 1.4 : 1,
            padding: rpx(14),
            justifyContent: 'center',
          }}
        >
          <Figure
            icon={Users}
            color={aiColors.violet}
            label="已触达客户"
            value={number(funnel.reached)}
          />
          <View
            style={{
              height: 1,
              backgroundColor: aiColors.border,
              marginVertical: rpx(10),
            }}
          />
          <Figure
            icon={MessageCircleMore}
            color={aiColors.primary}
            label="回复率"
            value={apdmPercent(funnel.replied, funnel.reached)}
          />
        </ApdmCard>
        <Tile
          wide={wide}
          icon={Star}
          color={aiColors.amber}
          label="产生线索"
          value={number(dashboard.leads)}
        />
        <Tile
          wide={wide}
          icon={Trophy}
          color={aiColors.green}
          label="已转化"
          value={number(funnel.converted)}
          note={`核销 ${number(funnel.redeemed)} 张券`}
        />
      </View>
    );
  },
);
